// src/api.js
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://tretacre-backend-production.up.railway.app/api';
const axiosInstance = axios.create({ baseURL });

const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'https://tretacre-backend-production.up.railway.app/api',
    headers: { 'Content-Type': 'application/json' },
});

console.log(`api.js: Initializing with baseURL=${api.defaults.baseURL}`);

// ===== AUTH INTERCEPTOR FOR BOTH INSTANCES =====

const authRequestInterceptor = (config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
};

const authResponseInterceptor = (error) => {
    if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        window.location.href = '/login';
    }
    return Promise.reject(error);
};

// ✅ Apply to `api`
api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use((response) => response, authResponseInterceptor);

// ✅ Also apply to `axiosInstance` (fixes the auth token issue)
axiosInstance.interceptors.request.use(authRequestInterceptor);
axiosInstance.interceptors.response.use((response) => response, authResponseInterceptor);

api.interceptors.request.use((config) => {
    console.log(`api.js: Sending ${config.method.toUpperCase()} to ${config.url}`);
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log(`api.js: Added Authorization: Bearer ${token.slice(0, 20)}...`);
    } else {
        console.log('api.js: No token found in localStorage');
    }
    return config;
});

// ✅ Also add auth interceptor to axiosInstance for consistency
axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const setAuthToken = (token, role, subscription, roles = []) => {
    if (token) {
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem('token', token);
        localStorage.setItem('role', role || 'client');
        localStorage.setItem('roles', JSON.stringify(roles));  // ✅ NEW
        localStorage.setItem('subscription', subscription || 'basic');
    } else {
        delete axiosInstance.defaults.headers.common['Authorization'];
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        localStorage.removeItem('roles');  // ✅ NEW
        localStorage.removeItem('subscription');
    }
};

export const login = async (email, password) => {
    const response = await axiosInstance.post('/login', { email, password });
    setAuthToken(
        response.data.token,
        response.data.role,
        response.data.subscription,
        response.data.roles || []   // ✅ Pass roles
    );
    return response.data;
};

export const logout = () => {
    setAuthToken(null);
};

// ✅ NEW: Get properties with enhanced filters
export const getProperties = async (filters = {}) => {
    // Support both Mumbai and Pune
    const params = { ...filters };
    
    // If city is not specified, default to both
    if (!params.city) {
        params.city = 'mumbai,pune';
    }
    
    const response = await axiosInstance.get('/properties', { params });
    return response.data;
};

// ✅ NEW: Get warehouse statistics
export const getWarehouseStats = async (city = 'pune') => {
    const response = await axiosInstance.get('/warehouse-stats', { params: { city } });
    return response.data;
};

// ✅ NEW: Get locations by city
export const getLocations = async (city = 'pune', propertyCategory = 'warehouse') => {
    const response = await axiosInstance.get('/locations', { params: { city, propertyCategory } });
    return response.data;
};

// ✅ NEW: Submit warehouse property
export const submitWarehouseProperty = async (formData) => {
    const response = await axiosInstance.post('/properties', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};


export const submitProperty = async (formData) => {
    const response = await axiosInstance.post('/properties', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const submitInsight = async (data) => {
    const response = await axiosInstance.post('/insights', data);
    return response.data;
};

export const getAveragePrice = async (sub_locality) => {
    const response = await axiosInstance.get('/calculator/average', { params: { sub_locality } });
    return response.data;
};

export const getUsers = async () => {
    const response = await axiosInstance.get('/users');
    return response.data;
};

export const approveUser = async (id) => {
    const response = await axiosInstance.patch(`/users/${id}/approve`);
    return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/users/${id}`, userData);
  return response.data;
};


export const deleteUser = async (id) => {
  const response = await api.delete(`/users/${id}`);
  return response.data;
};

export const approveProperty = async (id) => {
    const response = await axiosInstance.patch(`/properties/${id}/approve`);
    return response.data;
};

export const uploadJson = async (file) => {
    const formData = new FormData();
    formData.append('datafile', file);
    const response = await axiosInstance.post('/upload-json', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const exportProperties = async (filters = {}) => {
    try {
        const response = await api.get('/export', {
            params: filters,
            responseType: 'blob', // Handle binary data (CSV file)
        });
        console.log('api.js: Export response received, triggering download');

        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition
            ? contentDisposition.match(/filename="(.+)"/)?.[1] || 'properties.csv'
            : 'properties.csv';
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true, filename };
    } catch (error) {
        console.error('api.js: Error exporting properties:', error.response?.data || error.message);
        throw error;
    }
};
// src/api.js - Add these functions

export const exportProperty = (propertyId) => {
    return axios.get(`/api/export/property/${propertyId}`, {
        responseType: 'blob',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

export const exportComparison = (propertyIds) => {
    return axios.post('/api/export/compare', { propertyIds }, {
        responseType: 'blob',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    });
};

// Get user's properties with filters
export const getMyProperties = async (params = {}) => {
    const response = await api.get('/properties/my', { params });
    return response.data;
};

// Deactivate a property
export const deactivateProperty = async (id, reason, comment = '') => {
    const response = await api.patch(`/properties/${id}/deactivate`, { reason, comment });
    return response.data;
};

// Reactivate a property
export const reactivateProperty = async (id) => {
    const response = await api.patch(`/properties/${id}/reactivate`);
    return response.data;
};

// Get pending approvals (team lead + admin)
export const getPendingApprovals = async (params = {}) => {
    const response = await api.get('/properties/pending-approval', { params });
    return response.data;
};

// Reject a property
export const rejectProperty = async (id) => {
    const response = await api.patch(`/properties/${id}/reject`);
    return response.data;
};

// Request changes on a property
export const requestChanges = async (id, comment) => {
    const response = await api.patch(`/properties/${id}/request-changes`, { comment });
    return response.data;
};

// Export selected properties for client (transposed Excel)
export const exportClientSend = async (propertyIds) => {
    const response = await api.post('/export/client-send', { propertyIds }, {
        responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Extract filename from header or use default
    const contentDisposition = response.headers['content-disposition'];
    const filename = contentDisposition
        ? contentDisposition.match(/filename="?(.+?)"?$/)?.[1] || 'property-options.xlsx'
        : 'property-options.xlsx';

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
};

// Get a single property by ID (for edit mode)
export const getPropertyById = async (id) => {
    const response = await api.get(`/properties/${id}`);
    return response.data;
};

// Update an existing property (full update)
export const updateProperty = async (id, data) => {
    const response = await api.put(`/properties/${id}`, data);
    return response.data;
};

// ===== USER ROLE MANAGEMENT =====

// Get all roles for a user
export const getUserRoles = async (userId) => {
    const response = await api.get(`/users/${userId}/roles`);
    return response.data;
};

// Add a role to a user
export const addUserRole = async (userId, role, cities) => {
    const response = await api.post(`/users/${userId}/roles`, { role, cities });
    return response.data;
};

// Revoke a role from a user
export const revokeUserRole = async (userId, roleId) => {
    const response = await api.delete(`/users/${userId}/roles/${roleId}`);
    return response.data;
};

export default api;
